/**
 * announcement-marquee.js
 *
 * Continuous-scroll mode for the announcement bar.
 *
 * Horizon renders the announcement blocks once, via {% content_for 'blocks' %},
 * and Liquid cannot render them a second time. A seamless loop needs the
 * content repeated, so the repetition is done here: the original group is
 * cloned until one half of the track is at least as wide as the bar, then the
 * track is animated from 0 to -50%. Because half the track always covers the
 * viewport, the seam never shows.
 *
 * Speed is px/second, so the messages travel at the same rate no matter how
 * many there are or how long they get.
 */
class AnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.announcement-bar__track');
    this.group = this.querySelector('.announcement-bar__group');
    if (!this.track || !this.group) return;

    this.speed = parseFloat(this.getAttribute('speed')) || 60; // px per second
    this.build();

    // Re-measure when the width changes or a webfont swaps in — both change
    // how much room one group takes, and a stale measurement shows the seam.
    //
    // A ResizeObserver on this element alone proved unreliable: on a viewport
    // change it did not fire, so a 1600px window kept the 2 copies sized for
    // 814px and the loop showed a gap. The window resize event is the
    // dependable trigger; the observer stays as a backstop for layout shifts
    // that do not change the window (a drawer opening, a scrollbar appearing).
    this.onResize = () => this.schedule();
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('orientationchange', this.onResize, { passive: true });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.schedule());
      this.observer.observe(this);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.schedule());
    }
    // late-loading images or fonts can still shift widths after load
    window.addEventListener('load', this.onResize, { once: true });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    clearTimeout(this.timer);
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.build(), 150);
  }

  build() {
    // start from a single group every time so rebuilds don't stack clones
    this.track.querySelectorAll('.announcement-bar__group').forEach((g, i) => {
      if (i > 0) g.remove();
    });

    const groupWidth = this.group.getBoundingClientRect().width;
    const barWidth = this.getBoundingClientRect().width;
    if (!groupWidth || !barWidth) return;

    // one half of the track must be at least as wide as the bar
    const perHalf = Math.max(1, Math.ceil(barWidth / groupWidth));
    const total = perHalf * 2;

    const frag = document.createDocumentFragment();
    for (let i = 1; i < total; i++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      frag.appendChild(clone);
    }
    this.track.appendChild(frag);

    // constant px/second regardless of content length
    const distance = groupWidth * perHalf;
    this.style.setProperty('--marquee-duration', `${distance / this.speed}s`);
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
